
import { Component, Prop, Vue } from 'vue-property-decorator';
import OsCampaignForm from '@/components/shared/campaigns/OsCampaignForm.vue';
import {
  ICampaign,
  ICustomerSetting,
  ILink,
  IRecipient,
  IReportContentCategory,
  Recipientgroup,
  ReportAudience
} from '@/models/application';
import OsSheet from '@/components/core/sheet/OsSheet.vue';
import OsSheetContent from '@/components/core/sheet/OsSheetContent.vue';
import { customersApi } from '@/apis';
import OsLoadingIndicator from '@/components/core/OsLoadingIndicator.vue';
import OsAntesDisc from '@/components/chart/OsAntesDisc.vue';
import OsLinksOverview from '@/components/shared/links/OsLinksOverview.vue';
import OsRecipientsOverview from '@/components/shared/recipients/OsRecipientsOverview.vue';
import { AppDirtyUtil } from '@/utils/AppDirtyUtil';
import userModule from '@/store/UserModule';
import OsAlertBox from '@/components/core/OsAlertBox.vue';
@Component({
  components: {
    OsAlertBox,
    OsRecipientsOverview,
    OsLinksOverview,
    OsAntesDisc,
    OsLoadingIndicator,
    OsSheetContent,
    OsSheet,
    OsCampaignForm
  }
})
export default class CampaignCreate extends Vue {
  appDirtyUtil: AppDirtyUtil = new AppDirtyUtil();

  @Prop({ required: true, default: null })
  alreadyFetchedCampaign!: ICampaign | null;

  @Prop({ required: true, default: null })
  customerSetting!: ICustomerSetting | null;

  campaign: ICampaign | null = null;

  showCreateDialog = false;
  loading = false;
  customerId!: string;
  campaignId!: string;

  recipients: IRecipient[] = [];

  removedCategoriesForCompany: IReportContentCategory[] = [];
  addedCategoriesForCompany: IReportContentCategory[] = [];
  removedCategoriesForParticipants: IReportContentCategory[] = [];
  addedCategoriesForParticipants: IReportContentCategory[] = [];

  created() {
    this.customerId = userModule.state.customer!.id;
  }

  async mounted() {
    this.campaignId = this.$route.params.campaignid;

    if (this.alreadyFetchedCampaign) {
      this.campaign = this.alreadyFetchedCampaign;
    } else {
      this.campaign = await customersApi.getCampaignById(
        this.customerId,
        this.campaignId
      );
    }

    this.recipients = await customersApi.getCampaignRecipients(
      this.customerId,
      this.campaignId
    );
  }

  get isRecipientGroupParticipants(): boolean {
    return this.campaign?.recipientgroup === Recipientgroup.Participants;
  }

  async onSave() {
    this.loading = true;
    try {
      this.campaign!.customerId = this.customerId;

      this.campaign = await customersApi.updateCampaign(
        this.customerId,
        this.campaign!
      );

      await this.saveReportContentSegments();

      (this.$refs as any).campaignForm.reloadAssignedCategories();

      this.appDirtyUtil.init([
        this.campaign,
        this.addedCategoriesForCompany,
        this.removedCategoriesForCompany,
        this.addedCategoriesForParticipants,
        this.removedCategoriesForParticipants
      ]);
    } finally {
      this.loading = false;
    }
  }

  async saveReportContentSegments() {
    if (
      this.removedCategoriesForCompany &&
      this.removedCategoriesForCompany.length > 0
    ) {
      for (const c of this.removedCategoriesForCompany) {
        await customersApi.removeReportContentCategoryFromCampaign(
          this.customerId,
          this.campaignId,
          c.id,
          ReportAudience.Company
        );
      }
    }

    if (
      this.addedCategoriesForCompany &&
      this.addedCategoriesForCompany.length > 0
    ) {
      for (const c of this.addedCategoriesForCompany) {
        await customersApi.assignReportContentCategoryToCampaign(
          this.customerId,
          this.campaignId,
          c.id,
          ReportAudience.Company
        );
      }
    }

    if (
      this.removedCategoriesForParticipants &&
      this.removedCategoriesForParticipants.length > 0
    ) {
      for (const c of this.removedCategoriesForParticipants) {
        await customersApi.removeReportContentCategoryFromCampaign(
          this.customerId,
          this.campaignId,
          c.id,
          ReportAudience.Participant
        );
      }
    }

    if (
      this.addedCategoriesForParticipants &&
      this.addedCategoriesForParticipants.length > 0
    ) {
      for (const c of this.addedCategoriesForParticipants) {
        await customersApi.assignReportContentCategoryToCampaign(
          this.customerId,
          this.campaignId,
          c.id,
          ReportAudience.Participant
        );
      }
    }
  }

  async onDelete() {
    if (
      await this.$global.dialogs.confirm(
        this.$tc('form.campaign.delete_dialog.title'),
        this.$t('form.campaign.delete_dialog.message', {
          title: this.campaign!.title
        }) as string
      )
    ) {
      const result = await customersApi.deleteCampaignById(
        this.customerId,
        this.campaign!.id
      );
      if (result) {
        await this.$router.push(`/campaigns/`);
      }
    }
  }

  undoClicked() {
    this.campaign = this.appDirtyUtil.getOriginalObject()[0];
    this.addedCategoriesForCompany = this.appDirtyUtil.getOriginalObject()[1];
    this.removedCategoriesForCompany = this.appDirtyUtil.getOriginalObject()[2];
    this.addedCategoriesForParticipants =
      this.appDirtyUtil.getOriginalObject()[3];
    this.removedCategoriesForParticipants =
      this.appDirtyUtil.getOriginalObject()[4];
    this.appDirtyUtil.init([
      this.campaign,
      this.addedCategoriesForCompany,
      this.removedCategoriesForCompany,
      this.addedCategoriesForParticipants,
      this.removedCategoriesForParticipants
    ]);

    (this.$refs as any).campaignForm.reloadAssignedCategories();
  }

  onRecipientsCreate() {
    this.showCreateDialog = true;
  }

  onRecipientCreated(recipient: IRecipient) {
    this.recipients.push(recipient);
    this.showCreateDialog = false;
  }

  async deleteClicked(recipient: IRecipient) {
    if (
      await this.$global.dialogs.confirm(
        this.$tc('table.recipient.delete_dialog_header'),
        this.$t('table.recipient.delete_dialog_text', {
          emailaddress: recipient.emailaddress
        }) as string
      )
    ) {
      await this.deleteRecipient(recipient);
    }
  }
  async deleteRecipient(recipient: IRecipient) {
    const result = await customersApi.deleteCampaignRecipient(
      this.customerId,
      this.campaignId,
      recipient.id
    );

    if (result) {
      const index = this.recipients.findIndex((r) => r.id === recipient.id);
      this.recipients.splice(index, 1);

      this.$global.dialogs.toast(this.$tc('table.recipient.delete_success'), {
        color: 'success',
        icon: 'mdi-delete-outline'
      });
    } else {
      this.$global.dialogs.toast(this.$tc('table.recipient.delete_error'), {
        color: 'error',
        icon: 'mdi-delete-outline'
      });
    }
  }

  onTypeClicked(link: ILink) {
    if (link.participantId) {
      this.$router.push(
        `/campaigns/${this.campaignId}/participants/${link.participantId}`
      );
    }
  }

  goToParticipant(id: string) {
    this.$router.push(`/campaigns/${this.campaignId}/participants/${id}`);
  }
}
